/**
 * Clerk Recommendations Component - AJAX Approach
 * Fetches product recommendations from Clerk.io API,
 * then fetches pre-rendered product cards from Shopify for 100% design consistency.
 */

(function () {
  "use strict";

  const selectors = {
    section: "[data-clerk-section]",
    productsContainer: "[data-clerk-products]",
    placeholder: "[data-placeholder]",
    gridSlider: "[data-grid-slider]",
    navPrev: "[data-slider-prev]",
    navNext: "[data-slider-next]",
  };

  const classes = {
    hidden: "clerk-recommendations--empty",
  };

  /**
   * Initialize Clerk Recommendations Section
   */
  function initClerkRecommendations(sectionEl) {
    const clerkSection = sectionEl.querySelector(selectors.section);
    if (!clerkSection) return;

    const apiEndpoint = clerkSection.dataset.clerkApi;
    const configData = clerkSection.dataset.clerkConfig;
    const productsContainer = clerkSection.querySelector(
      selectors.productsContainer
    );
    const gridSlider = clerkSection.querySelector(selectors.gridSlider);

    if (!apiEndpoint || !configData || !productsContainer) {
      console.error("[Clerk] Missing required data attributes");
      hideSection(sectionEl);
      return;
    }

    let config;
    try {
      config = JSON.parse(configData);
    } catch (e) {
      console.error("[Clerk] Failed to parse config:", e);
      hideSection(sectionEl);
      return;
    }

    // Check if Clerk is available
    if (typeof window.Clerk !== "function") {
      console.error("[Clerk] Clerk SDK not loaded");
      hideSection(sectionEl);
      return;
    }

    // Call Clerk API
    window.Clerk(
      "call",
      apiEndpoint,
      config,
      function (response) {
        handleClerkResponse(response, productsContainer, gridSlider, sectionEl);
      },
      function (error) {
        console.error("[Clerk] API error:", error);
        hideSection(sectionEl);
      }
    );
  }

  /**
   * Handle Clerk API response
   * Clerk returns product data including URLs - extract handles from URLs
   */
  async function handleClerkResponse(
    response,
    productsContainer,
    gridSlider,
    sectionEl
  ) {
    const products = response.product_data || [];

    if (products.length === 0) {
      console.log("[Clerk] No products returned");
      hideSection(sectionEl);
      return;
    }

    // Extract handles from Clerk product URLs
    const productHandles = products
      .map(function (product) {
        if (product.url) {
          // Extract handle from URL like "/products/penny-jumper-knit-brown"
          const urlParts = product.url.split("/products/");
          return urlParts[1] || null;
        }
        return null;
      })
      .filter(function (handle) {
        return handle !== null;
      });

    if (productHandles.length === 0) {
      console.log("[Clerk] No valid product handles found");
      hideSection(sectionEl);
      return;
    }

    try {
      // Clear placeholders
      const placeholders = productsContainer.querySelectorAll(
        selectors.placeholder
      );
      placeholders.forEach((el) => el.remove());

      // Fetch and render product cards (in parallel)
      const renderPromises = productHandles.map((handle, index) =>
        fetchAndRenderProductCard(
          handle,
          productsContainer,
          index,
          products[index].id
        )
      );

      await Promise.all(renderPromises);

      // Register click tracking with Clerk
      if (typeof window.Clerk === "function") {
        window.Clerk("click", "*[data-clerk-product-id]");
      }

      // Register new product cards with deferred-media-loader
      document.dispatchEvent(new CustomEvent('load-more:complete'));

      // Initialize grid-slider
      if (gridSlider) {
        setTimeout(function () {
          const event = new CustomEvent("theme:grid-slider:init", {
            bubbles: true,
          });
          gridSlider.dispatchEvent(event);
        }, 100);
      }

      // Initialize linked products (color swatches)
      if (typeof window.initLinkedProducts === "function") {
        setTimeout(function () {
          window.initLinkedProducts();
        }, 150);
      }

      // Setup navigation
      setupNavigation(sectionEl, gridSlider);
    } catch (error) {
      console.error("[Clerk] Error rendering products:", error);
      hideSection(sectionEl);
    }
  }

  /**
   * Fetch Shopify product data (.js endpoint)
   * Returns: { id, handle, title, variants, etc. }
   */
  async function fetchShopifyProduct(productId) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      if (!response.ok) {
        console.warn(`[Clerk] Product ${productId} not found (404)`);
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error(`[Clerk] Failed to fetch product ${productId}:`, error);
      return null;
    }
  }

  /**
   * Fetch pre-rendered product card HTML and insert into DOM
   */
  async function fetchAndRenderProductCard(productHandle, container, index) {
    try {
      // Fetch pre-rendered HTML using Section Rendering API
      const url = `/products/${productHandle}?section_id=api-product-card`;
      const response = await fetch(url);

      if (!response.ok) {
        console.warn(
          `[Clerk] Failed to fetch product card for ${productHandle}`
        );
        return;
      }

      const html = await response.text();

      // Parse HTML to extract the product grid item
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const productCard = doc.querySelector("[data-grid-item]");

      if (productCard) {
        // Add Clerk product ID for click tracking
        const productLink = productCard.querySelector("[data-product-link]");
        if (productLink) {
          const productIdMatch = productHandle.match(/\d+$/);
          if (productIdMatch) {
            productCard.setAttribute(
              "data-clerk-product-id",
              productIdMatch[0]
            );
          }
        }

        // Insert into container
        container.insertAdjacentHTML("beforeend", productCard.outerHTML);
      } else {
        console.warn(
          `[Clerk] No product card found in HTML for ${productHandle}`
        );
      }
    } catch (error) {
      console.error(
        `[Clerk] Error rendering product card for ${productHandle}:`,
        error
      );
    }
  }

  /**
   * Setup arrow navigation
   */
  function setupNavigation(sectionEl, slider) {
    const prevButton = sectionEl.querySelector(selectors.navPrev);
    const nextButton = sectionEl.querySelector(selectors.navNext);

    if (!slider || !prevButton || !nextButton) return;

    function scrollSlider(direction) {
      const scrollAmount = slider.offsetWidth - 16;
      const newPosition =
        direction === "next"
          ? slider.scrollLeft + scrollAmount
          : slider.scrollLeft - scrollAmount;

      slider.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
    }

    function updateButtons() {
      const isAtStart = slider.scrollLeft <= 0;
      const isAtEnd =
        slider.scrollLeft + slider.offsetWidth >= slider.scrollWidth - 10;

      prevButton.disabled = isAtStart;
      nextButton.disabled = isAtEnd;
    }

    prevButton.addEventListener("click", function () {
      scrollSlider("prev");
    });

    nextButton.addEventListener("click", function () {
      scrollSlider("next");
    });

    slider.addEventListener("scroll", updateButtons);

    // Initial state
    updateButtons();

    // Update on resize
    window.addEventListener("resize", updateButtons);
  }

  /**
   * Hide section
   */
  function hideSection(sectionEl) {
    sectionEl.classList.add(classes.hidden);
  }

  /**
   * Initialize all sections on page load
   */
  function initAll() {
    const sections = document.querySelectorAll(
      '[data-section-type="clerk-recommendations"]'
    );
    sections.forEach(function (section) {
      initClerkRecommendations(section);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Also init on Shopify section load (for theme editor)
  document.addEventListener("shopify:section:load", function (event) {
    const section = event.target;
    if (section.dataset.sectionType === "clerk-recommendations") {
      initClerkRecommendations(section);
    }
  });
})();
